import React from 'react'

import { useTranslation } from 'react-i18next'

import ForgotPassword from '@components/music/title-details/purchase-title-dialog/SignInRequired/ForgotPassword'
import useSignInForm from '@components/music/title-details/purchase-title-dialog/SignInRequired/hooks/useSignInForm'
import Button from '@components/shared/Button'
import ErrorToast from '@components/shared/ErrorToast'
import Input from '@components/shared/form/Input'
import facebookLogo from '@images/FacebookLogo.png'
import googleLogo from '@images/GoogleLogo.png'

interface Props {
  purchaseQuantity: number
  titleId: string
}

const SignInForm: React.FC<Props> = ({ purchaseQuantity, titleId }: Props) => {
  const { t } = useTranslation()
  const { actions, password, form, code } = useSignInForm(purchaseQuantity, titleId)

  if (password.forgot) {
    return (
      <>
        <h1 className="mb-4 font-semibold text-center text-h3">{t('content.signInToPurchase')}</h1>
        <ForgotPassword
          outdatedPassword={password.outdated}
          goBack={() => password.setForgot(false)}
        />
      </>
    )
  }

  return (
    <>
      <h1 className="mb-4 font-semibold text-center text-h3">{t('content.signInToPurchase')}</h1>
      {form.submitError && (
        <ErrorToast error={form.submitError} onClearError={form.clearSubmitError} />
      )}
      <div className="flex flex-col items-center justify-between w-full sm:items-start sm:flex-row">
        {/* Sign In Form */}
        <form
          className="flex flex-col flex-1"
          onSubmit={async (e) => {
            e.preventDefault()
            await actions.signInWithEmail()
          }}>
          {/* Username */}
          <Input
            name="username"
            label={t('content.username')}
            required
            value={form.data.username ?? ''}
            error={form.errors.username}
            onChange={(e) => form.updateData({ username: e.target.value })}
            solidBackground
          />

          {/* Password */}
          <Input
            name="password"
            label={t('content.password')}
            type="password"
            required
            value={form.data.password ?? ''}
            error={form.errors.password}
            onChange={(e) => form.updateData({ password: e.target.value })}
            solidBackground
          />

          {/* Forgot Password */}
          <div className="mb-4">
            <button type="button" className="orange-link" onClick={() => password.setForgot(true)}>
              {t('content.forgotPassword')}
            </button>
          </div>

          {/* Code */}
          {code.required && (
            <Input
              name="code"
              label={t('content.twoFactorCode')}
              value={form.data.code ?? ''}
              required={code.required}
              onChange={(e) => form.updateData({ code: e.target.value })}
              solidBackground
              ref={code.ref}
            />
          )}

          <Button type="submit" disabled={form.submitting}>
            {t('action.signIn')}
          </Button>
        </form>

        {/* Or Separator */}
        <span className="mx-6 mt-8 text-sm opacity-75 sm:mt-20">{t('content.orCapital')}</span>

        {/* OAuth Sign In */}
        <div className="flex flex-col mt-8">
          <Button
            variant="outline"
            className="flex flex-row items-center w-full my-2"
            onClick={actions.signInWithGoogle}>
            <img src={googleLogo} alt="Google" className="w-5 mr-2" />
            <span className="text-md">{t('content.signInWithGoogle')}</span>
          </Button>
          <Button
            variant="outline"
            className="flex flex-row items-center w-full my-2"
            onClick={actions.signInWithFacebook}>
            <img src={facebookLogo} alt="Facebook" className="w-5 mr-2" />
            <span className="text-md">{t('content.signInWithFacebook')}</span>
          </Button>
        </div>
      </div>
    </>
  )
}

export default SignInForm
